import { useQuery } from '@apollo/client'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { GET_PLATFORMS_WITH_SUBJECTS } from 'queries/subject'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { formatPlatformsWithKeys } from 'utils/formatPlatformsWithKeys'
import { PlatformSlug } from 'constants/consts'
import {
  findAllWithSubjectsTabs,
  findAllWithSubjectsTabs_findAllWithSubjectsTabs_subjects as Subject,
} from '__generatedTypes__/findAllWithSubjectsTabs'

export const getInitialSelectedSubjectId = (subjectId?: string | null, tabIndex?: string | number | null) => {
  if (!subjectId) {
    return ''
  }

  return `${subjectId}-${tabIndex ?? 1}`
}

export const SUBJECT_TABS_CONFIG: Record<string, { count: number }> = {
  'organic-chemistry': {
    count: 2,
  },
}

export type SubjectExtendedFull = Omit<Subject & { tabId: string; tabIndex: number }, '__typename'>

export type SubjectExtended = Omit<SubjectExtendedFull, 'icon' | 'classroomTab'>

export type Props = {
  initialSelectedSubjectId?: string | null
  autoSelectFirstSubject?: boolean
  isQuestionBanks?: boolean
  filterSubjects?: (subject: Subject) => boolean
  tabIndex?: number
}

export const usePlatformsAndSubjects = ({
  initialSelectedSubjectId = null,
  autoSelectFirstSubject = true,
  isQuestionBanks = false,
  filterSubjects,
  tabIndex = 1,
}: Props = {}) => {
  const { platform } = useParams()
  const currentPlatformSlug = platform ? String(platform) : null

  const [selectedPlatformKey, setSelectedPlatformKey] = useState<string | null>(currentPlatformSlug)
  const [selectedSubjectTabId, setSelectedSubjectTabId] = useState<string | null>(initialSelectedSubjectId)

  const { data, loading } = useQuery<findAllWithSubjectsTabs>(GET_PLATFORMS_WITH_SUBJECTS)

  const platforms = useMemo(
    () => formatPlatformsWithKeys(data?.findAllWithSubjectsTabs || []),
    [data?.findAllWithSubjectsTabs]
  )

  const selectedPlatform = useMemo(() => {
    if (selectedPlatformKey) {
      return platforms.find(({ slug }) => slug === selectedPlatformKey) ?? null
    }

    return null
  }, [selectedPlatformKey, platforms])

  const subjects = useMemo(() => {
    if (!filterSubjects) {
      return selectedPlatform?.subjects ?? []
    }

    return (selectedPlatform?.subjects ?? []).filter(filterSubjects)
  }, [selectedPlatform?.subjects, filterSubjects])

  const extendedSubjects = useMemo(
    () =>
      subjects.reduce((acc, subject) => {
        const subjectConfig = SUBJECT_TABS_CONFIG[subject.slug as string]

        const extendedSubject = {
          ...subject,
          tabId: isQuestionBanks ? getInitialSelectedSubjectId(subject.id, tabIndex) : subject.id,
          tabIndex,
        }

        if (isQuestionBanks && subjectConfig && subjectConfig.count > 1) {
          const additionalTabs = new Array(subjectConfig.count - 1).fill(0).map((_, i) => ({
            ...extendedSubject,
            tabName: `${subject.tabName} ${i + 2}`,
            tabId: `${subject.id}-${i + 2}`,
            tabIndex: tabIndex + 1 + i,
          }))

          return [...acc, extendedSubject, ...additionalTabs]
        }

        return [...acc, extendedSubject]
      }, [] as SubjectExtendedFull[]),
    [subjects]
  )

  const selectedSubject = useMemo(
    () => extendedSubjects?.find((subject) => subject.tabId === selectedSubjectTabId) ?? null,
    [extendedSubjects, selectedSubjectTabId]
  )

  useEffect(() => {
    if (platform) {
      setSelectedPlatformKey(platform)
    }
  }, [platform])

  useEffect(() => {
    if (autoSelectFirstSubject && !loading && extendedSubjects.length && !selectedSubject) {
      setSelectedSubjectTabId(extendedSubjects[0].tabId)
    }
  }, [autoSelectFirstSubject, loading, extendedSubjects, selectedSubject])

  const handleSelectPlatform = useCallback((platformOrKey: Platform | string | null) => {
    if (!platformOrKey) {
      setSelectedPlatformKey(null)
    } else if (typeof platformOrKey === 'string') {
      setSelectedPlatformKey(platformOrKey)
    } else {
      setSelectedPlatformKey(platformOrKey.slug)
    }
  }, [])

  const handleSelectSubject = useCallback((subjectOrTabId: SubjectExtended | string | null) => {
    if (!subjectOrTabId) {
      setSelectedSubjectTabId(null)
    } else if (typeof subjectOrTabId === 'string') {
      setSelectedSubjectTabId(subjectOrTabId)
    } else {
      setSelectedSubjectTabId(subjectOrTabId.tabId)
    }
  }, [])

  return {
    platforms,
    selectedPlatformSlug: selectedPlatformKey as PlatformSlug,
    selectedPlatform,
    selectPlatform: handleSelectPlatform,
    subjects: extendedSubjects,
    selectedSubject,
    selectSubject: handleSelectSubject,
    loading,
  }
}
